import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { AlertCircle, Info, AlertTriangle, XCircle } from 'lucide-react'
import { formatDateTimeCompact } from '@/lib/dateTime'

interface LogEntry {
  id: string
  timestamp: string
  level: string
  logger: string
  message: string
  raw: string
  uploadedAt: string
}

interface DeviceLogsProps {
  groupId: string
  deviceId: string
}

// How often to poll for fresh device logs (replaces the Firestore onSnapshot).
const POLL_INTERVAL_MS = 5000

export function DeviceLogs({ groupId, deviceId }: DeviceLogsProps) {
  const { t } = useTranslation()
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [levelFilter, setLevelFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')

  // TODO backend: getDeviceLogs — this callable does not exist on the AWS
  // backend yet. It should return the latest device logs
  // (`{ success: boolean, logs: LogEntry[] }`) for the given group/device,
  // newest first, capped at ~200 entries.
  const fetchLogs = useCallback(async () => {
    try {
      const fn = apiCallable('getDeviceLogs')
      const res = await fn({ groupId, deviceId, limit: 200 })
      const data = res.data as { success?: boolean; logs?: LogEntry[] }
      setLogs(data.logs || [])
      setLoading(false)
    } catch (error) {
      console.error('Error fetching logs:', error)
      setLoading(false)
    }
  }, [groupId, deviceId])

  // Poll for logs (replaces the Firestore onSnapshot listener).
  const fetchRef = useRef(fetchLogs)
  fetchRef.current = fetchLogs
  useEffect(() => {
    setLoading(true)
    fetchRef.current()
    const interval = setInterval(() => fetchRef.current(), POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [groupId, deviceId])

  const getLevelIcon = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return <XCircle className="h-4 w-4 text-[#7a3325]" />
      case 'WARNING':
        return <AlertTriangle className="h-4 w-4 text-gold" />
      case 'INFO':
        return <Info className="h-4 w-4 text-ink-2" />
      default:
        return <AlertCircle className="h-4 w-4 text-ink-soft" />
    }
  }

  const getLevelBadge = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return <Badge variant="destructive">{level}</Badge>
      case 'WARNING':
        return <Badge variant="warning">{level}</Badge>
      case 'INFO':
        return <Badge variant="default">{level}</Badge>
      default:
        return <Badge variant="secondary">{level}</Badge>
    }
  }

  const filteredLogs = logs.filter((log) => {
    const matchesLevel = levelFilter === 'all' || log.level.toUpperCase() === levelFilter.toUpperCase()
    const matchesSearch = !searchQuery ||
      log.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.logger.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesLevel && matchesSearch
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('deviceLogs.noLogs')}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <Select value={levelFilter} onValueChange={setLevelFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('deviceLogs.filterByLevel')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('deviceLogs.allLevels')}</SelectItem>
            <SelectItem value="ERROR">Error</SelectItem>
            <SelectItem value="WARNING">Warning</SelectItem>
            <SelectItem value="INFO">Info</SelectItem>
            <SelectItem value="DEBUG">Debug</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder={t('deviceLogs.searchLogs')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
      </div>

      {/* Logs Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">{t('deviceLogs.timestamp')}</TableHead>
              <TableHead className="w-[100px]">{t('deviceLogs.level')}</TableHead>
              <TableHead className="w-[200px]">{t('deviceLogs.logger')}</TableHead>
              <TableHead>{t('deviceLogs.message')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="font-mono text-xs">
                  {formatDateTimeCompact(log.timestamp, log.timestamp)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getLevelIcon(log.level)}
                    {getLevelBadge(log.level)}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {log.logger}
                </TableCell>
                <TableCell className="text-sm">
                  {log.message}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="text-xs text-muted-foreground text-center">
        {t('deviceLogs.showing', { filtered: filteredLogs.length, total: logs.length })}
      </div>
    </div>
  )
}
